import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { formatDate as fmtDate, formatDateTime as fmtDateTime } from '../lib/utils';

/**
 * Returns the user's effective timezone + helpers pre-bound to it.
 *
 *   const { timezone, formatDateTime, formatDate } = useUserTimezone();
 *   formatDateTime(submission.completed_at);  // rendered in user's saved zone
 *
 * Falls back to the browser-detected zone when the user hasn't saved one,
 * so behaviour is identical to before for new users.
 */
export function useUserTimezone() {
  const { user } = useAuth();

  const browserTimezone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
      return 'UTC';
    }
  }, []);

  const timezone = user?.timezone || browserTimezone;

  return useMemo(() => ({
    timezone,
    isUserSet: Boolean(user?.timezone),
    formatDate: (d) => fmtDate(d, timezone),
    formatDateTime: (d) => fmtDateTime(d, timezone),
  }), [timezone, user?.timezone]);
}

export default useUserTimezone;
